import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Lock, Mail, ShieldAlert, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../lib/auth.tsx';
import { supabase } from '../lib/db.ts';
import { Turnstile, type TurnstileRef } from '../components/common/Turnstile.tsx';

export function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<TurnstileRef>(null);
  const navigate = useNavigate();
  const { signIn, signOut } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Wait for Turnstile verification
    if (!captchaToken) {
      setLoading(false);
      setError('Please complete the security verification above before signing in.');
      return;
    }

    try {
      // Attempt to sign in
      await signIn(email, password);
      
      // Get user data to check role
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      
      // Verify admin role before redirecting
      if (currentUser?.app_metadata?.role === 'admin' || currentUser?.app_metadata?.role === 'superuser') {
        // Import logger
        const { logger } = await import('../services/logger.ts');
        logger.info('Admin login successful', {
          email,
          userId: currentUser?.id,
          role: currentUser?.app_metadata?.role,
        });
        navigate('/admin/dashboard', { replace: true });
      } else {
        // Import logger
        const { logger } = await import('../services/logger.ts');
        logger.logFailedLogin(email, 'Insufficient admin privileges', {
          reason: 'insufficient_privileges',
          userId: currentUser?.id,
          attemptedRole: 'admin',
        });
        setError('You do not have admin privileges');
        // Sign out if not an admin
        await signOut();
      }
    } catch (err) {
      // Import logger
      const { logger } = await import('../services/logger.ts');
      logger.logFailedLogin(email, 'Invalid admin credentials', {
        reason: 'invalid_credentials',
        context: 'admin_login',
        error: err instanceof Error ? err.message : 'Unknown error',
      });
      setError('Invalid admin credentials');
      // Reset CAPTCHA on error
      setCaptchaToken(null);
      captchaRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center">
      <div className="bg-gray-800 p-8 rounded-lg shadow-xl w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <ShieldAlert className="w-12 h-12 text-wayja-teal" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Admin Portal</h1>
          <p className="text-gray-400">Sign in to access admin controls</p>
        </div>

        {error && (
          <div className={`mb-4 p-3 rounded ${
            error.includes('complete the security verification') || error.includes('Security verification')
              ? 'bg-yellow-900 border border-yellow-700 text-yellow-100'
              : 'bg-red-900 border border-red-700 text-red-100'
          }`}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5" />
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 w-full rounded-md bg-gray-700 border-gray-600 text-white px-4 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
                placeholder="Enter your email"
                required
                disabled={loading}
              />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5" />
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 pr-10 w-full rounded-md bg-gray-700 border-gray-600 text-white px-4 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
                placeholder="Enter your password"
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                id="remember-me"
                type="checkbox"
                className="h-4 w-4 text-wayja-teal focus:ring-wayja-teal border-gray-600 rounded bg-gray-700"
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-300">
                Remember me
              </label>
            </div>
            <Link to="/auth/reset-password" className="text-sm text-wayja-teal hover:text-wayja-teal">
              Forgot password?
            </Link>
          </div>

          {/* Cloudflare Turnstile verification - visible to users */}
          <div className="flex flex-col items-center space-y-2">
            <Turnstile
              ref={captchaRef}
              size="normal"
              onVerify={(token) => {
                setCaptchaToken(token);
                // Clear any verification-related errors when successful
                if (error.includes('Security verification') || error.includes('complete the security verification')) {
                  setError('');
                }
              }}
              onError={() => {
                setCaptchaToken(null);
                setError('Security verification failed. Please click the checkbox above to retry.');
              }}
              onExpire={() => {
                setCaptchaToken(null);
                setError('Security verification expired. Please complete the verification above.');
              }}
            />
          </div>

          <button
            type="submit"
            className="w-full bg-wayja-teal text-white py-2 px-4 rounded-md hover:bg-opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading || !captchaToken}
          >
            {loading ? 'Signing in...' : !captchaToken ? 'Complete verification to sign in' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}